import { useEffect, useRef, useState, type FormEvent } from 'react';
import {
  Loader2,
  Upload,
  FileText,
  FlaskConical,
  Share2,
  X,
  Trash2,
  Plus,
  History,
  RotateCcw,
} from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { toast } from 'sonner';
import { api, ApiError } from '@/services/api';
import { useApplicants } from '@/contexts/ApplicantContext';
import { Card } from '@/components/ui/Card';
import { formatDate } from '@/lib/format';
import { ProfileEditor } from '@/app/resumes/ProfileEditor';

interface TestField {
  label: string;
  field_type: string;
  required: boolean;
  cache_hit: { answer: string; source: string; slug_match: string } | null;
}
interface TestResult {
  supported: boolean;
  ats: string;
  slug?: string;
  job_id?: string;
  fields: TestField[];
  cache_hit_count: number;
  total_required: number;
  message?: string;
}

interface Customized {
  applicant_id: string;
  slug: string;
  dir: string;
  mtime: string;
  has_docx: boolean;
  has_pdf: boolean;
}

export default function ResumesPage() {
  const { current } = useApplicants();
  const [customized, setCustomized] = useState<Customized[]>([]);
  const [seed, setSeed] = useState<{ data: unknown; path: string } | null>(null);
  const [seedText, setSeedText] = useState('');
  const [seedDirty, setSeedDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const [testUrl, setTestUrl] = useState('');
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [testing, setTesting] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const [c, s] = await Promise.allSettled([
        api.get<{ items: Customized[] }>(`/resumes/customized?applicant_id=${current}`),
        api.get<{ data: unknown; path: string }>(`/smart-fill/seed?applicant_id=${current}`),
      ]);
      if (c.status === 'fulfilled') setCustomized(c.value.items);
      if (s.status === 'fulfilled') {
        setSeed(s.value);
        setSeedText(JSON.stringify(s.value.data, null, 2));
        setSeedDirty(false);
      }
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current]);

  async function onUpload(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    setUploading(true);
    try {
      await api.postForm(`/resumes/master?applicant_id=${current}`, fd);
      toast.success(`Uploaded ${file.name}`);
      if (fileRef.current) fileRef.current.value = '';
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'upload_failed');
    } finally {
      setUploading(false);
    }
  }

  async function runSmartFillTest() {
    if (!testUrl.trim()) return;
    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.post<TestResult>('/smart-fill/test', { url: testUrl.trim() });
      setTestResult(r);
      if (r.message) toast(r.message);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'test_failed');
    } finally {
      setTesting(false);
    }
  }

  async function saveSeed() {
    let parsed: unknown;
    try {
      parsed = JSON.parse(seedText);
    } catch {
      toast.error('Invalid JSON — fix syntax before saving');
      return;
    }
    try {
      await api.post(`/smart-fill/seed?applicant_id=${current}`, parsed);
      toast.success('Seed saved');
      setSeedDirty(false);
      load();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'save_failed');
    }
  }

  return (
    <div className="space-y-5">
      <h2 className="text-xl font-semibold tracking-tight">Resumes — {current}</h2>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Master resume">
          <form onSubmit={onUpload} className="flex items-center gap-2">
            <input
              ref={fileRef}
              type="file"
              accept=".docx"
              required
              className="text-sm text-gray-700 file:mr-3 file:rounded-md file:border-0 file:bg-primary-50 file:px-3 file:py-1.5 file:text-primary-700 hover:file:bg-primary-100"
            />
            <button type="submit" disabled={uploading} className="btn-primary">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              Replace
            </button>
            <a href={`/api/resumes/master?applicant_id=${current}`} className="btn-secondary">
              <FileText className="h-4 w-4" />
              Download
            </a>
          </form>
          <p className="mt-2 text-xs text-gray-500">
            DOCX is used as the source for every customization run. Replace only when you've made
            cross-role updates.
          </p>
        </Card>

        <Card title={`Customized (${customized.length})`}>
          {loading ? (
            <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
          ) : customized.length === 0 ? (
            <p className="text-sm text-gray-500">No customized resumes for this applicant yet.</p>
          ) : (
            <ul className="max-h-72 space-y-1 overflow-y-auto text-sm">
              {customized.map((c) => (
                <li key={c.slug} className="flex items-center justify-between gap-2">
                  <span className="flex-1 truncate text-gray-700 dark:text-gray-200">{c.slug}</span>
                  <span className="shrink-0 text-xs text-gray-400">
                    {c.has_pdf ? 'pdf' : ''} {c.has_docx ? 'docx' : ''} · {formatDate(c.mtime)}
                  </span>
                  {(c.has_pdf || c.has_docx) && (
                    <>
                      <ShareButton resume={c} applicantId={current} />
                      <VersionsButton slug={c.slug} applicantId={current} />
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <ShareLinksCard applicantId={current} />

      {/* Everything below is power-user. Onboarding wizard sets the profile,
          the cover-letter library is rarely tweaked, smart-fill internals are
          for debugging. Collapse so the main page is just upload + download. */}
      <details className="rounded-lg border border-gray-200 dark:border-gray-800">
        <summary className="cursor-pointer px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-200">
          Advanced
        </summary>
        <div className="space-y-4 border-t border-gray-100 p-4 dark:border-gray-800">
          <CoverLetterTemplatesCard applicantId={current} />

          <ProfileEditor applicantId={current} />

          <Card title="Test smart-fill against a URL">
        <p className="mb-2 text-xs text-gray-500">
          Paste a Greenhouse application URL to see which questions the cache covers
          before workers attempt it. Other ATSes are filled live and not testable here.
        </p>
        <div className="flex gap-2">
          <input
            type="url"
            value={testUrl}
            onChange={(e) => setTestUrl(e.target.value)}
            placeholder="https://job-boards.greenhouse.io/example/jobs/123…"
            className="input flex-1"
          />
          <button onClick={runSmartFillTest} disabled={testing} className="btn-primary">
            {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <FlaskConical className="h-4 w-4" />}
            Test
          </button>
        </div>
        {testResult && (
          <div className="mt-4 space-y-2">
            <div className="flex flex-wrap gap-2 text-xs">
              <span className="badge-blue">{testResult.ats}</span>
              {testResult.slug && <span className="badge-gray">{testResult.slug}/{testResult.job_id}</span>}
              <span className={testResult.cache_hit_count > 0 ? 'badge-green' : 'badge-amber'}>
                {testResult.cache_hit_count}/{testResult.fields.length} cache-filled
              </span>
              {testResult.message && <span className="text-amber-600">{testResult.message}</span>}
            </div>
            {testResult.fields.length > 0 && (
              <ul className="max-h-80 space-y-1 overflow-y-auto text-sm">
                {testResult.fields.map((f, i) => (
                  <li
                    key={i}
                    className={`flex items-start justify-between rounded-md px-3 py-1.5 ${
                      f.cache_hit
                        ? 'bg-green-50 dark:bg-green-500/10'
                        : 'bg-amber-50 dark:bg-amber-500/10'
                    }`}
                  >
                    <div>
                      <div className="font-medium text-gray-800 dark:text-gray-100">
                        {f.label}{' '}
                        {f.required && <span className="text-red-600">*</span>}
                      </div>
                      <div className="text-xs text-gray-500">{f.field_type}</div>
                    </div>
                    {f.cache_hit ? (
                      <div className="text-right text-xs">
                        <div className="text-green-700 dark:text-green-300">
                          {f.cache_hit.answer.slice(0, 80)}
                        </div>
                        <div className="text-gray-400">
                          {f.cache_hit.slug_match} / {f.cache_hit.source}
                        </div>
                      </div>
                    ) : (
                      <span className="text-xs text-amber-700 dark:text-amber-300">no cache</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </Card>

      <Card
        title="Smart-fill seed"
        action={
          <div className="flex gap-2">
            {seedDirty && <span className="text-xs text-amber-700">unsaved</span>}
            <button onClick={saveSeed} disabled={!seedDirty} className="btn-primary text-xs">
              Save
            </button>
          </div>
        }
      >
        <p className="mb-2 text-xs text-gray-500">
          Source: <code className="text-xs">{seed?.path}</code>. The applier loads these on boot.
          Edit as JSON; YAML is round-tripped on save.
        </p>
        <textarea
          value={seedText}
          onChange={(e) => {
            setSeedText(e.target.value);
            setSeedDirty(true);
          }}
          className="input min-h-[420px] font-mono text-xs"
          spellCheck={false}
        />
      </Card>
        </div>
      </details>
    </div>
  );
}

// ── Share button ───────────────────────────────────────────────────────────
interface ShareRow {
  token: string;
  applicant_id: string;
  job_id: number | null;
  file_path: string;
  file_kind: string;
  label: string | null;
  created_at: string;
  expires_at: string | null;
  view_count: number;
  max_views: number;
  revoked: number;
  last_viewed_at: string | null;
  url: string;
}

function ShareButton({ resume, applicantId }: { resume: Customized; applicantId: string }) {
  const [busy, setBusy] = useState(false);
  async function create() {
    setBusy(true);
    try {
      const file_kind = resume.has_pdf ? 'pdf' : 'docx';
      const file_path =
        `all_resumes/${applicantId === 'peter' ? '' : applicantId + '/'}${resume.slug}/` +
        (file_kind === 'pdf' ? 'Peter_Chen_Resume.pdf' : 'Peter_Chen_Resume.docx');
      const r = await api.post<ShareRow>('/share', {
        applicant_id: applicantId,
        file_path,
        file_kind,
        label: resume.slug,
        ttl_hours: 7 * 24,
      });
      await navigator.clipboard.writeText(r.url).catch(() => {});
      toast.success('Link copied to clipboard', {
        description: `${r.url} (expires in 7 days)`,
      });
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'share_failed');
    } finally {
      setBusy(false);
    }
  }
  return (
    <button onClick={create} disabled={busy} className="btn-secondary text-xs" title="Create share link">
      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Share2 className="h-3 w-3" />}
    </button>
  );
}

function ShareLinksCard({ applicantId }: { applicantId: string }) {
  const [rows, setRows] = useState<ShareRow[]>([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get<{ items: ShareRow[] }>(`/share?applicant_id=${applicantId}`);
      setRows(r.items);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicantId]);

  async function revoke(token: string) {
    if (!confirm('Revoke this share link? Anyone with the URL will get 403.')) return;
    try {
      await api.delete(`/share/${token}`);
      toast.success('Revoked');
      load();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'revoke_failed');
    }
  }

  return (
    <Card title={`Active share links (${rows.length})`}>
      {loading ? (
        <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-500">
          No active share links. Click the <Share2 className="inline h-3 w-3" /> button on a customized
          resume to create one — useful for sending a tailored PDF to a recruiter.
        </p>
      ) : (
        <ul className="space-y-1 text-xs">
          {rows.map((r) => (
            <li
              key={r.token}
              className="flex items-center gap-2 rounded-md bg-gray-50 px-2 py-1.5 dark:bg-gray-800/60"
            >
              <span className="flex-1 truncate text-gray-700 dark:text-gray-200">
                {r.label ?? r.file_path}
              </span>
              <span className="shrink-0 text-gray-400">
                {r.view_count}/{r.max_views} views · expires {formatDate(r.expires_at ?? '')}
              </span>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(r.url).catch(() => {});
                  toast.success('Copied');
                }}
                className="btn-secondary text-xs"
                title="Copy link"
              >
                Copy
              </button>
              <button
                onClick={() => revoke(r.token)}
                className="rounded-md p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
                title="Revoke"
              >
                <X className="h-3 w-3" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// ── Resume version history ─────────────────────────────────────────────────
interface ResumeVersion {
  id: number;
  applicant_id: string;
  slug: string;
  file_kind: 'pdf' | 'docx';
  version: number;
  path: string;
  sha256: string;
  size_bytes: number;
  label: string | null;
  snapshot_at: string;
}

function VersionsButton({ slug, applicantId }: { slug: string; applicantId: string }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<ResumeVersion[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get<{ items: ResumeVersion[] }>(
        `/resume-versions?applicant_id=${applicantId}&slug=${encodeURIComponent(slug)}`,
      );
      setRows(r.items);
    } finally {
      setLoading(false);
    }
  }

  async function openModal() {
    setOpen(true);
    load();
  }

  async function snapshot() {
    setBusy(true);
    try {
      const r = await api.post<{
        written: ResumeVersion[];
        skipped: Array<{ kind: string; reason: string }>;
      }>('/resume-versions', { applicant_id: applicantId, slug });
      if (r.written.length === 0 && r.skipped.length > 0) {
        toast(`No changes since last snapshot (${r.skipped.map((s) => s.kind).join(', ')})`);
      } else {
        toast.success(
          `Saved ${r.written.length} version${r.written.length === 1 ? '' : 's'}`,
        );
      }
      load();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'snapshot_failed');
    } finally {
      setBusy(false);
    }
  }

  async function restore(id: number, version: number) {
    if (
      !confirm(
        `Restore v${version}? The current file will be auto-snapshotted first so this is reversible.`,
      )
    ) {
      return;
    }
    try {
      await api.post(`/resume-versions/${id}/restore`);
      toast.success(`Restored v${version}`);
      load();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'restore_failed');
    }
  }

  async function del(id: number) {
    if (!confirm('Delete this version? File on disk will be removed.')) return;
    try {
      await api.delete(`/resume-versions/${id}`);
      toast.success('Deleted');
      load();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'delete_failed');
    }
  }

  return (
    <>
      <button
        onClick={openModal}
        className="btn-secondary text-xs"
        title="View / snapshot resume versions"
      >
        <History className="h-3 w-3" />
      </button>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={`Versions — ${slug}`}
        footer={
          <>
            <button onClick={() => setOpen(false)} className="btn-secondary">
              Close
            </button>
            <button onClick={snapshot} disabled={busy} className="btn-primary">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Snapshot now
            </button>
          </>
        }
      >
        {loading ? (
          <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
        ) : rows.length === 0 ? (
          <p className="text-sm text-gray-500">
            No saved versions yet. Click <em>Snapshot now</em> to archive the current PDF + DOCX
            before your next regeneration.
          </p>
        ) : (
          <ul className="space-y-1 text-sm">
            {rows.map((v) => (
              <li
                key={v.id}
                className="flex items-center gap-2 rounded-md bg-gray-50 px-2 py-1.5 dark:bg-gray-800/60"
              >
                <span className="shrink-0 font-mono text-xs text-gray-500">
                  v{v.version} · {v.file_kind}
                </span>
                <span className="flex-1 truncate text-gray-700 dark:text-gray-200">
                  {v.label ?? `(no label)`}{' '}
                  <span className="text-xs text-gray-400">{formatDate(v.snapshot_at)}</span>
                </span>
                <span className="shrink-0 text-xs text-gray-400">
                  {(v.size_bytes / 1024).toFixed(1)} KB
                </span>
                <button
                  onClick={() => restore(v.id, v.version)}
                  className="btn-secondary text-xs"
                  title="Restore this version to the main file"
                >
                  <RotateCcw className="h-3 w-3" />
                </button>
                <button
                  onClick={() => del(v.id)}
                  className="rounded-md p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
                  title="Delete version"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </Modal>
    </>
  );
}

// ── Cover letter library ───────────────────────────────────────────────────
interface CoverLetterTemplate {
  id: number;
  applicant_id: string;
  name: string;
  tone: string | null;
  body: string;
  created_at: string;
  updated_at: string;
}

function CoverLetterTemplatesCard({ applicantId }: { applicantId: string }) {
  const [rows, setRows] = useState<CoverLetterTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [tone, setTone] = useState('');
  const [body, setBody] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get<{ items: CoverLetterTemplate[] }>(
        `/cover-letter-templates?applicant_id=${applicantId}`,
      );
      setRows(r.items);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicantId]);

  function reset() {
    setEditingId(null);
    setName('');
    setTone('');
    setBody('');
  }

  async function save() {
    if (!name.trim() || !body.trim()) return;
    try {
      if (editingId == null) {
        await api.post('/cover-letter-templates', {
          applicant_id: applicantId,
          name: name.trim(),
          tone: tone.trim() || null,
          body,
        });
      } else {
        await api.patch(`/cover-letter-templates/${editingId}`, {
          name: name.trim(),
          tone: tone.trim() || null,
          body,
        });
      }
      toast.success(editingId == null ? 'Template added' : 'Template updated');
      reset();
      load();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'save_failed');
    }
  }

  async function edit(t: CoverLetterTemplate) {
    setEditingId(t.id);
    setName(t.name);
    setTone(t.tone ?? '');
    setBody(t.body);
  }

  async function del(id: number) {
    if (!confirm('Delete this template?')) return;
    try {
      await api.delete(`/cover-letter-templates/${id}`);
      toast.success('Deleted');
      load();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'delete_failed');
    }
  }

  return (
    <Card title={`Cover letter library (${rows.length})`}>
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          {loading ? (
            <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
          ) : rows.length === 0 ? (
            <p className="text-sm text-gray-500">
              No templates yet. Save a reusable starting paragraph (intro, "why CreatoRain", closing)
              and apply it on the Application Detail page.
            </p>
          ) : (
            <ul className="max-h-80 space-y-1 overflow-y-auto text-sm">
              {rows.map((t) => (
                <li key={t.id} className="rounded-md bg-gray-50 px-2 py-1.5 dark:bg-gray-800/60">
                  <div className="flex items-center gap-2">
                    <span className="flex-1 truncate font-medium text-gray-800 dark:text-gray-100">
                      {t.name}
                    </span>
                    {t.tone && (
                      <span className="badge-blue text-xs">{t.tone}</span>
                    )}
                    <button onClick={() => edit(t)} className="btn-secondary text-xs">
                      Edit
                    </button>
                    <button
                      onClick={() => del(t.id)}
                      className="rounded-md p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
                      title="Delete"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                  <p className="mt-1 line-clamp-2 text-xs text-gray-500">{t.body}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="space-y-2">
          <div className="text-xs font-medium text-gray-700 dark:text-gray-300">
            {editingId == null ? 'Add template' : `Editing #${editingId}`}
          </div>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Name (e.g. PM intro)"
            className="input text-sm"
          />
          <input
            value={tone}
            onChange={(e) => setTone(e.target.value)}
            placeholder="Tone (formal / casual / creator-focused) — optional"
            className="input text-sm"
          />
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={6}
            placeholder="Body — use {{company}} / {{role}} placeholders if you like"
            className="input text-sm"
          />
          <div className="flex gap-2">
            <button
              onClick={save}
              disabled={!name.trim() || !body.trim()}
              className="btn-primary text-xs"
            >
              {editingId == null ? (
                <>
                  <Plus className="h-3 w-3" />
                  Add
                </>
              ) : (
                'Update'
              )}
            </button>
            {editingId != null && (
              <button onClick={reset} className="btn-secondary text-xs">
                Cancel
              </button>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}
